import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

// ============================================
// CONFIGURATION
// ============================================

// Translation backend options
export type TranslationBackend = 'google' | 'openai';

// Default backend - change this to switch translation providers
export const DEFAULT_TRANSLATION_BACKEND: TranslationBackend = 'openai';

// Backend function names in Supabase
const BACKEND_FUNCTIONS: Record<TranslationBackend, string> = {
  google: 'google-translate',
  openai: 'llm-translate',
};

// ============================================
// CACHE
// ============================================

// Simple in-memory cache with LRU-like behavior
const translationCache = new Map<string, string>();
const MAX_CACHE_SIZE = 1000;

const getCacheKey = (text: string, targetLang: string, backend: TranslationBackend): string => {
  return `${backend}:${targetLang}:${text}`;
};

const getFromCache = (text: string, targetLang: string, backend: TranslationBackend): string | undefined => {
  return translationCache.get(getCacheKey(text, targetLang, backend));
};

const setInCache = (text: string, targetLang: string, backend: TranslationBackend, translation: string): void => {
  if (translationCache.size >= MAX_CACHE_SIZE) {
    const firstKey = translationCache.keys().next().value;
    if (firstKey) translationCache.delete(firstKey);
  }
  translationCache.set(getCacheKey(text, targetLang, backend), translation);
};

// ============================================
// MAIN TRANSLATION HOOK
// ============================================

interface UseTranslateOptions {
  texts: string[];
  enabled?: boolean;
  backend?: TranslationBackend;
  context?: string; // Optional context for LLM (e.g., "UI labels", "professional services")
}

interface UseTranslateResult {
  translatedTexts: string[];
  isLoading: boolean;
  error: Error | null;
}

export const useTranslate = ({
  texts,
  enabled = true,
  backend = DEFAULT_TRANSLATION_BACKEND,
  context,
}: UseTranslateOptions): UseTranslateResult => {
  const { i18n } = useI18nTranslation();
  const [translatedTexts, setTranslatedTexts] = useState<string[]>(texts);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const previousTextsRef = useRef<string[]>([]);
  const previousLangRef = useRef<string>('');

  const currentLanguage = i18n.language;
  const isHebrew = currentLanguage === 'he';

  const translateTexts = useCallback(async () => {
    // Skip translation if Hebrew or not enabled
    if (isHebrew || !enabled || texts.length === 0) {
      setTranslatedTexts(texts);
      return;
    }

    // Check cache first
    const cachedResults: (string | undefined)[] = texts.map(t =>
      t ? getFromCache(t, currentLanguage, backend) : t
    );

    const allCached = cachedResults.every((r, i) => r !== undefined || !texts[i]);

    if (allCached) {
      setTranslatedTexts(cachedResults.map((r, i) => r ?? texts[i]));
      return;
    }

    // Find texts that need translation
    const textsToTranslate: string[] = [];
    const indexMap: number[] = [];

    texts.forEach((text, index) => {
      if (text && !getFromCache(text, currentLanguage, backend)) {
        textsToTranslate.push(text);
        indexMap.push(index);
      }
    });

    if (textsToTranslate.length === 0) {
      setTranslatedTexts(texts.map(t => t ? getFromCache(t, currentLanguage, backend) ?? t : t));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const functionName = BACKEND_FUNCTIONS[backend];

      // Auto-detect source language (for DB content that may be in English or Hebrew)
      const detectedSourceLang = detectSourceLanguage(textsToTranslate);

      const { data, error: fetchError } = await supabase.functions.invoke(functionName, {
        body: {
          texts: textsToTranslate,
          targetLanguage: currentLanguage,
          sourceLanguage: detectedSourceLang, // Use detected language instead of hardcoded 'he'
          ...(backend === 'openai' && context ? { context } : {}),
        }
      });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (data?.translations) {
        // Cache the new translations
        data.translations.forEach((translation: string, i: number) => {
          const originalText = textsToTranslate[i];
          if (originalText && translation) {
            setInCache(originalText, currentLanguage, backend, translation);
          }
        });

        // Build final result with all translations
        const result = texts.map(text => {
          if (!text) return text;
          return getFromCache(text, currentLanguage, backend) ?? text;
        });

        setTranslatedTexts(result);
      }
    } catch (err) {
      console.error('Translation error:', err);
      setError(err instanceof Error ? err : new Error('Translation failed'));
      setTranslatedTexts(texts); // Fall back to original texts
    } finally {
      setIsLoading(false);
    }
  }, [texts, currentLanguage, isHebrew, enabled, backend, context]);

  useEffect(() => {
    const textsChanged = JSON.stringify(texts) !== JSON.stringify(previousTextsRef.current);
    const langChanged = currentLanguage !== previousLangRef.current;

    if (textsChanged || langChanged) {
      previousTextsRef.current = texts;
      previousLangRef.current = currentLanguage;
      translateTexts();
    }
  }, [translateTexts, texts, currentLanguage]);

  return { translatedTexts, isLoading, error };
};

// ============================================
// HELPER HOOKS
// ============================================

// Translate a single text
export const useTranslateText = (
  text: string,
  enabled = true,
  backend: TranslationBackend = DEFAULT_TRANSLATION_BACKEND
): {
  translatedText: string;
  isLoading: boolean;
} => {
  const { translatedTexts, isLoading } = useTranslate({
    texts: text ? [text] : [],
    enabled,
    backend,
  });

  return {
    translatedText: translatedTexts[0] ?? text,
    isLoading
  };
};

// Translate multiple fields
export const useTranslateFieldsLLM = <T extends Record<string, string | undefined>>(
  fields: T,
  enabled = true,
  backend: TranslationBackend = DEFAULT_TRANSLATION_BACKEND
): {
  translatedFields: T;
  isLoading: boolean;
} => {
  const keys = Object.keys(fields);
  const values = keys.map(k => fields[k] ?? '');

  const { translatedTexts, isLoading } = useTranslate({
    texts: values,
    enabled,
    backend,
  });

  const translatedFields = {} as T;
  keys.forEach((key, index) => {
    (translatedFields as any)[key] = translatedTexts[index] || fields[key];
  });

  return { translatedFields, isLoading };
};

// ============================================
// DROPDOWN OPTIONS
// ============================================

interface DropdownOption {
  id: string;
  label: string;
  [key: string]: any;
}

export const useTranslatedOptionsLLM = <T extends DropdownOption>(
  options: T[],
  enabled = true,
  backend: TranslationBackend = DEFAULT_TRANSLATION_BACKEND
): {
  translatedOptions: T[];
  isLoading: boolean;
} => {
  const { i18n } = useI18nTranslation();
  const isHebrew = i18n.language === 'he';

  const labels = options.map(opt => opt.label);

  const { translatedTexts, isLoading } = useTranslate({
    texts: labels,
    enabled: enabled && !isHebrew && options.length > 0,
    backend,
    context: 'dropdown options for professional services',
  });

  const translatedOptions = useMemo(() =>
    options.map((opt, index) => ({
      ...opt,
      label: (!isHebrew && translatedTexts[index]) || opt.label
    })),
    [options, translatedTexts, isHebrew]
  );

  return { translatedOptions, isLoading };
};

// ============================================
// INPUT TRANSLATION (User input → Hebrew for DB)
// ============================================

export const isHebrewText = (text: string): boolean => {
  if (!text?.trim()) return true;

  const hebrewPattern = /[\u0590-\u05FF]/g;
  const letterPattern = /\p{L}/gu; // letters of ANY script: Latin, Cyrillic, Arabic, CJK\u2026

  const hebrewCount = (text.match(hebrewPattern) || []).length;
  const letterCount = (text.match(letterPattern) || []).length;
  const nonHebrewLetters = letterCount - hebrewCount;

  // Too few letters to judge (numbers, punctuation, emoji) \u2192 treat as Hebrew (skip translation)
  if (letterCount < 3) return true;

  // Hebrew only if Hebrew letters dominate every other script combined \u2014 so Cyrillic
  // (Russian) and Arabic are correctly detected as non-Hebrew and get translated.
  return hebrewCount >= nonHebrewLetters;
};

// Detect source language of text (for database content that may be in English or Hebrew)
export const detectSourceLanguage = (texts: string[]): 'he' | 'en' => {
  // Sample first few texts to determine language
  const sampleTexts = texts.slice(0, Math.min(5, texts.length));
  const hebrewCount = sampleTexts.filter(text => isHebrewText(text)).length;

  // If majority are Hebrew, assume Hebrew; otherwise assume English
  return hebrewCount > sampleTexts.length / 2 ? 'he' : 'en';
};

export const translateToHebrew = async (
  texts: string[],
  backend: TranslationBackend = DEFAULT_TRANSLATION_BACKEND
): Promise<string[]> => {
  const validTexts = texts.filter(t => t?.trim());
  if (validTexts.length === 0) return texts;

  const needsTranslation = validTexts.some(text => !isHebrewText(text));
  if (!needsTranslation) return texts;

  try {
    const functionName = BACKEND_FUNCTIONS[backend];

    const { data, error } = await supabase.functions.invoke(functionName, {
      body: {
        texts: validTexts,
        targetLanguage: 'he',
        sourceLanguage: 'auto',
        ...(backend === 'openai' ? { context: 'user input for home services request' } : {}),
      }
    });

    if (error || !data?.translations) {
      console.error('Translation to Hebrew failed:', error);
      return texts;
    }

    let translatedIdx = 0;
    return texts.map(t => {
      if (!t?.trim()) return t;
      const translated = data.translations[translatedIdx++];
      return translated || t;
    });
  } catch (err) {
    console.error('Translation error:', err);
    return texts;
  }
};

export const useTranslateToHebrewLLM = (backend: TranslationBackend = DEFAULT_TRANSLATION_BACKEND) => {
  const [isTranslating, setIsTranslating] = useState(false);

  const translateFields = async <T extends Record<string, string | undefined>>(
    fields: T,
    keysToTranslate: (keyof T)[]
  ): Promise<T> => {
    setIsTranslating(true);
    try {
      const textsToTranslate = keysToTranslate.map(k => fields[k] || '');
      const translated = await translateToHebrew(textsToTranslate, backend);

      const result = { ...fields };
      keysToTranslate.forEach((key, idx) => {
        if (translated[idx]) {
          (result as any)[key] = translated[idx];
        }
      });
      return result;
    } finally {
      setIsTranslating(false);
    }
  };

  return { translateFields, isTranslating, isHebrewText };
};

// ============================================
// BACKWARD-COMPATIBLE EXPORTS
// These mirror the old useGoogleTranslate.ts API
// ============================================

// Main hook - alias for useTranslate
export const useGoogleTranslate = (options: { texts: string[]; enabled?: boolean }) => {
  return useTranslate({
    texts: options.texts,
    enabled: options.enabled,
  });
};

// Field translation - alias for useTranslateFieldsLLM
export const useTranslateFields = <T extends Record<string, string | undefined>>(
  fields: T,
  enabled = true
) => {
  return useTranslateFieldsLLM(fields, enabled);
};

// Options translation - alias for useTranslatedOptionsLLM
export const useTranslatedOptions = <T extends DropdownOption>(
  options: T[],
  enabled = true
) => {
  return useTranslatedOptionsLLM(options, enabled);
};

// Hebrew input translation - alias for useTranslateToHebrewLLM
export const useTranslateToHebrew = () => {
  return useTranslateToHebrewLLM();
};

// Standalone function for options translation
export const translateOptionsToUserLanguage = async <T extends DropdownOption>(
  options: T[],
  targetLanguage: string,
  backend: TranslationBackend = DEFAULT_TRANSLATION_BACKEND
): Promise<T[]> => {
  if (targetLanguage === 'he' || options.length === 0) {
    return options;
  }

  const labels = options.map(opt => opt.label);

  try {
    const functionName = BACKEND_FUNCTIONS[backend];

    // Auto-detect source language
    const detectedSourceLang = detectSourceLanguage(labels);

    const { data, error } = await supabase.functions.invoke(functionName, {
      body: {
        texts: labels,
        targetLanguage,
        sourceLanguage: detectedSourceLang, // Use detected language
        ...(backend === 'openai' ? { context: 'dropdown options for professional services' } : {}),
      }
    });

    if (error || !data?.translations) {
      return options;
    }

    return options.map((opt, index) => ({
      ...opt,
      label: data.translations[index] || opt.label
    }));
  } catch {
    return options;
  }
};

export default useTranslate;
